import {CSSProperties, useCallback, useEffect, useRef, useState} from "react";
import {cvv_types} from '../../proto/compiled';
import styled from "styled-components";
import {CvvTextInput} from "./text_input";
import {CvvInputField} from "./input";
import {Map, Marker} from "@vis.gl/react-google-maps";
import {observer} from "mobx-react-lite";
import {MdLocationOn} from "react-icons/md";
import {useAutocompleteSuggestions} from "./use_autocomplete_suggestions";

// Autocomplete options that mimic the legacy behavior:
//   - Hard-restrict suggestions to SI + the neighbouring countries the legacy
//     componentRestrictions allowed (it, hr, at, hu) via `includedRegionCodes`.
//   - Softly prefer Slovenian places via a 50 km `locationBias` circle around
//     Ljubljana so e.g. "Trg republike, Ljubljana" outranks same-named streets
//     in Croatia.
//   - `region: 'si'` is an additional ranking hint per the Places API docs.
//   Note: `locationBias` and `locationRestriction` are mutually exclusive, so
//   the geographic restriction has to go through `includedRegionCodes`.
export const SLOVENIA_AUTOCOMPLETE_OPTIONS: Partial<google.maps.places.AutocompleteRequest> = {
  includedRegionCodes: ['si', 'it', 'hr', 'at', 'hu'],
  locationBias: {
    center: {lat: 46.0569, lng: 14.5058}, // Ljubljana
    radius: 50000,
  },
  region: 'si',
};

const INVALID_MSG = 'S spustnega seznama izberite naslov s hišno številko. Če naslova ni na seznamu, izberite najbližji naslov s seznama in kasneje pod opombe vpišite vaš pravi naslov.';
const PROMPT_MSG = 'Izberite naslov s spustnega seznama. Če naslova ni na seznamu, izberite najbližji naslov s seznama in kasneje pod opombe vpišite vaš pravi naslov.';

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const SuggestionsAnchor = styled.div`
    position: relative;
`;

const SuggestionsList = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    margin: 0;
    padding: 0;
    list-style: none;
    background: white;
    border: 1px solid #ddd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    max-height: 260px;
    overflow-y: auto;
`;

const SuggestionItem = styled.li`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.2;

    &:hover, &[data-active="true"] {
        background: #f3f3f3;
    }
`;

const SuggestionIcon = styled(MdLocationOn)`
    color: #5f6368;
    flex-shrink: 0;
    font-size: 18px;
`;

const SuggestionText = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const SuggestionMain = styled.span`
    color: #202124;
    font-weight: 500;
`;

const SuggestionSecondary = styled.span`
    color: #5f6368;
    margin-left: 4px;
`;

interface GmapsAddressPickerProps {
  address: cvv_types.IAddress;
  onPlaceSelected: (place: google.maps.places.Place) => any;
  error?: string;
  noMap?: boolean;
  label?: string;
}

export const GmapsAddressPicker = (props: GmapsAddressPickerProps) => {
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState(props.address.address || '');
  const [selected, setSelected] = useState<google.maps.places.Place | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  // The `query` state is what feeds the autocomplete hook. We keep it separate
  // from the input value so that after a selection we can stop new suggestions
  // from being fetched while still showing the formatted address in the input.
  const [query, setQuery] = useState('');
  const {suggestions, resetSession} = useAutocompleteSuggestions(query, SLOVENIA_AUTOCOMPLETE_OPTIONS);

  // Reset highlight whenever the suggestion list changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [suggestions]);

  // Keep the highlighted item visible inside the scroll container.
  useEffect(() => {
    if (activeIndex >= 0) {
      itemRefs.current[activeIndex]?.scrollIntoView({block: 'nearest'});
    }
  }, [activeIndex]);

  // Commit-style invalidation: if the user edited the input after selecting,
  // but never picked a new suggestion, drop the stale proto data and surface
  // the prompt error. Triggered on Enter (no highlight) and click-outside.
  const commitInvalidation = useCallback(() => {
    if (!selected) return;
    if (address === selected.formattedAddress) return;
    props.address.address = '';
    props.address.lat = undefined;
    props.address.lng = undefined;
    props.address.city = undefined;
    props.address.country = undefined;
    props.address.line1 = undefined;
    props.address.postalCode = undefined;
    props.address.state = undefined;
    props.address.street = undefined;
    props.address.streetNumber = undefined;
    setSelected(null);
    if (!error) setError(PROMPT_MSG);
  }, [selected, address, error, props.address]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
        commitInvalidation();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [commitInvalidation]);

  const onSelect = useCallback(async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!suggestion.placePrediction) return;
    setShowSuggestions(false);
    setQuery('');

    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents', 'location'],
    });
    // fetchFields invalidates the session token; reset for any further searches.
    resetSession();

    if (place.formattedAddress) {
      setAddress(place.formattedAddress);
    }

    const hasStreetNumber = place.addressComponents?.some((c) =>
      c.types.includes('street_number'),
    );
    if (!place.addressComponents || !hasStreetNumber) {
      setError(INVALID_MSG);
    } else {
      setError(null);
      setSelected(place);
      props.onPlaceSelected(place);
    }
  }, [props, resetSession]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setShowSuggestions(true);
        setActiveIndex((i) => (i + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setShowSuggestions(true);
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        break;
      case 'Enter':
        if (showSuggestions && activeIndex >= 0 && activeIndex < suggestions.length) {
          e.preventDefault();
          onSelect(suggestions[activeIndex]);
        } else {
          setShowSuggestions(false);
          commitInvalidation();
        }
        break;
      case 'Escape':
        if (showSuggestions) {
          e.preventDefault();
          setShowSuggestions(false);
        }
        break;
    }
  };

  return <Container ref={containerRef}>
    <CvvInputField label={props.label ?? 'NASLOV'} error={error || props.error} input={
      <SuggestionsAnchor>
        <CvvTextInput
          autoComplete={'address-line1'}
          $invalid={!!(error || props.error)}
          value={address}
          placeholder={''}
          onChange={(e) => {
            const value = e.target.value;
            setAddress(value);
            setQuery(value);
            setShowSuggestions(true);
            if (!value) {
              // Clearing the input must also clear the underlying proto,
              // otherwise the form keeps the previously selected address.
              props.address.address = '';
              props.address.lat = undefined;
              props.address.lng = undefined;
              props.address.city = undefined;
              props.address.country = undefined;
              props.address.line1 = undefined;
              props.address.postalCode = undefined;
              props.address.state = undefined;
              props.address.street = undefined;
              props.address.streetNumber = undefined;
              setSelected(null);
            }
            if (selected == null && !error) {
              setError(PROMPT_MSG);
            }
          }}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          onKeyDown={onKeyDown}
        />
        {showSuggestions && suggestions.length > 0 && (
          <SuggestionsList>
            {suggestions.map((s, i) => {
              const main = s.placePrediction?.mainText?.text ?? s.placePrediction?.text?.text ?? '';
              const secondary = s.placePrediction?.secondaryText?.text;
              return (
                <SuggestionItem
                  key={i}
                  ref={(el) => { itemRefs.current[i] = el; }}
                  data-active={i === activeIndex ? 'true' : undefined}
                  onMouseEnter={() => setActiveIndex(i)}
                  // Prevent input blur from firing before click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSelect(s)}
                >
                  <SuggestionIcon/>
                  <SuggestionText>
                    <SuggestionMain>{main}</SuggestionMain>
                    {secondary && <SuggestionSecondary>{secondary}</SuggestionSecondary>}
                  </SuggestionText>
                </SuggestionItem>
              );
            })}
          </SuggestionsList>
        )}
      </SuggestionsAnchor>
    }/>
    {!props.noMap && <GMapsShowAddress address={props.address} containerStyle={{
      width: '100%',
      height: '230px'
    }}/>}
  </Container>;
}

interface GmapsShowAddressProps {
  address?: cvv_types.IAddress;
  zoom?: number;
  containerStyle?: CSSProperties;
}

// Fallback map center when no address is selected.
const DEFAULT_MAP_CENTER = {lat: 46.0511, lng: 14.5051};

export const GMapsShowAddress = observer((props: GmapsShowAddressProps) => {
  // Proto-generated numeric fields default to 0 (not null/undefined), so we
  // treat falsy lat/lng the same as "no value" — otherwise the map centers
  // on (0, 0) in the Gulf of Guinea and a marker is shown there.
  const hasAddress = !!props.address?.lat && !!props.address?.lng;
  const center = hasAddress
    ? {lat: props.address!.lat!, lng: props.address!.lng!}
    : DEFAULT_MAP_CENTER;

  return <div tabIndex={-1}>
    <Map
      style={props.containerStyle}
      center={center}
      zoom={props.zoom ?? 15}
      mapTypeId={'roadmap'}
      disableDefaultUI
      gestureHandling={'none'}
      keyboardShortcuts={false}
    >
      {hasAddress && <Marker position={center}/>}
    </Map>
  </div>;
});